// src/pages/components/TaskCard.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { tasksApi, Task } from '../api/tasks';
import StatusBadge from './StatusBadge';
import { useUserInfo } from '../hooks/useUserInfo';
import { usePermissionGuard } from '../hooks/usePermissionGuard';
import TaskEdit from '../pages/Task/TaskEdit';
import { JourneyStrip } from './JourneyStrip';

interface TaskCardProps {
  task: Task;
  onRefresh: () => void;
}

function TaskCard({ task, onRefresh }: TaskCardProps) {
  const { guard, showError, showSuccess } = usePermissionGuard();
  const navigate = useNavigate();
  const [updating, setUpdating] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  // Custom confirmation modal states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  const [showBlockPrompt, setShowBlockPrompt] = useState(false);
  const [blockReason, setBlockReason] = useState('');
  const [pendingAction, setPendingAction] = useState<{ type: string; status?: string } | null>(null);

  const { getUserName, getUserRole, currentUser } = useUserInfo();

  const handleStatusQuickChange = async (e: React.MouseEvent, newStatus: string) => {
    e.stopPropagation();

    await guard(async () => {
      setUpdating(true);
      await tasksApi.changeStatus(task.id, { status: newStatus });
      showSuccess('Status Updated', `Task status changed to ${newStatus}`);
      onRefresh();
      setUpdating(false);
    }, 'CANNOT_MODIFY_OTHERS_TASK');
  };

  const handleDelete = async () => {
    await guard(async () => {
      await tasksApi.delete(task.id);
      showSuccess('Task Deleted', 'Task has been deleted successfully');
      onRefresh();
      setShowDeleteConfirm(false);
    }, 'CANNOT_DELETE_OTHERS_TASK');
  };

  const handleComplete = async () => {
    await guard(async () => {
      await tasksApi.changeStatus(task.id, { status: 'DONE' });
      showSuccess('Task Completed', 'Task has been marked as completed');
      onRefresh();
      setShowCompleteConfirm(false);
    }, 'CANNOT_MODIFY_OTHERS_TASK');
  };

  const handleBlock = async () => {
    if (!blockReason.trim()) {
      showError('CANNOT_BLOCK_WITHOUT_REASON', 'Please provide a reason for blocking this task');
      return;
    }

    await guard(async () => {
      await tasksApi.changeStatus(task.id, { status: 'BLOCKED', blocked_reason: blockReason });
      showSuccess('Task Blocked', 'Task has been blocked');
      onRefresh();
      setShowBlockPrompt(false);
      setBlockReason('');
    }, 'CANNOT_MODIFY_OTHERS_TASK');
  };

  const handleQuickAction = (e: React.MouseEvent, action: string) => {
    e.stopPropagation();
    setPendingAction({ type: action });

    switch (action) {
      case 'start':
        handleStatusQuickChange(e, 'IN_PROGRESS');
        break;
      case 'complete':
        setShowCompleteConfirm(true);
        break;
      case 'block':
        setShowBlockPrompt(true);
        break;
      default:
        break;
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'No due date';
    const date = new Date(dateStr);
    const today = new Date();
    const diffDays = Math.ceil((date.getTime() - today.getTime()) / (1000 * 3600 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays < 0) return `${Math.abs(diffDays)} days overdue`;
    return date.toLocaleDateString();
  };

  const isOverdue = task.is_overdue && task.status !== 'DONE' && task.status !== 'CANCELLED';
  const isAssignedToMe = task.primary_assignee_id === currentUser?.id;
  const canStart = task.status === 'TODO' && (isAssignedToMe || currentUser?.role === 'admin');

  const priorityColors = {
    URGENT: 'bg-gradient-to-r from-red-500 to-red-600 text-white shadow-md',
    HIGH: 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-sm',
    MEDIUM: 'bg-gradient-to-r from-yellow-500 to-yellow-600 text-white shadow-sm',
    LOW: 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-sm',
  };

  const priorityIcons = { 
    URGENT: '🔴',
    HIGH: '🟠',
    MEDIUM: '🟡',
    LOW: '🟢',
  };

  return (
    <>
      <div className="relative">
        <div
          onClick={() => navigate(`/tasks/${task.id}`)}
          className="group bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 p-5 cursor-pointer border border-gray-100 hover:border-blue-200 transform hover:-translate-y-1 relative overflow-hidden"
        >
          {/* Priority Strip */}
          <div className={`absolute top-0 left-0 w-1 h-full ${task.priority === 'URGENT' ? 'bg-red-500' : task.priority === 'HIGH' ? 'bg-orange-500' : task.priority === 'MEDIUM' ? 'bg-yellow-500' : 'bg-green-500'}`} />

          {/* Header with Menu */}
          <div className="flex justify-between items-start mb-3">
            {/* Title */}
            <h3 className="font-semibold text-gray-900 line-clamp-2 flex-1 pr-2 group-hover:text-blue-600 transition-colors">
              {task.title}
            </h3>

            {/* Three-dot menu */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowMenu(!showMenu);
              }}
              className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-100 rounded-lg transition-all"
            >
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
              </svg>
            </button>

            {/* Dropdown Menu */}
            {showMenu && (
              <div className="absolute right-12 top-12 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10 min-w-32">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowEditModal(true);
                    setShowMenu(false);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDeleteConfirm(true);
                    setShowMenu(false);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete
                </button>
              </div>
            )}
          </div>

          {/* Status and Priority Badges */}
          <div className="flex gap-2 mb-3 flex-wrap">
            <StatusBadge status={task.status} />
            <span className={`px-2 py-1 rounded-lg text-xs font-medium ${priorityColors[task.priority as keyof typeof priorityColors] || 'bg-gray-100 text-gray-800'} flex items-center gap-1`}>
              <span>{priorityIcons[task.priority as keyof typeof priorityIcons]}</span>
              {task.priority}
            </span>
            {isOverdue && (
              <span className="px-2 py-1 rounded-lg text-xs font-medium bg-gradient-to-r from-red-100 to-red-200 text-red-800 border border-red-300 shadow-sm animate-pulse flex items-center gap-1">
                ⚠️ Overdue
              </span>
            )}
          </div>

          {/* Assignee Info */}
          {task.primary_assignee_id && (
            <div className="flex items-center gap-2 mb-3 text-xs">
              <div className="flex items-center gap-2 bg-gray-50 px-2 py-1 rounded-lg">
                <div className="w-5 h-5 bg-gradient-to-br from-blue-400 to-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                  {getUserName(task.primary_assignee_id).charAt(0).toUpperCase()}
                </div>
                <span className="text-gray-600">
                  {getUserName(task.primary_assignee_id)}
                  {isAssignedToMe && <span className="ml-1 text-blue-600 font-medium">(You)</span>}
                </span>
              </div>
              {task.primary_assignee_id && (
                <span className="text-xs text-gray-400">
                  • {getUserRole(task.primary_assignee_id)}
                </span>
              )}
            </div>
          )}

          {/* Description Preview */}
          {task.description && (
            <p className="text-gray-600 text-sm mb-3 line-clamp-2">
              {task.description.length > 100 ? `${task.description.substring(0, 100)}...` : task.description}
            </p>
          )}

          {/* Tags */}
          {task.tags && task.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-3">
              {task.tags.slice(0, 3).map((tag, i) => (
                <span key={i} className="text-xs text-gray-400">#{tag}</span>
              ))}
              {task.tags.length > 3 && (
                <span className="text-xs text-gray-400">+{task.tags.length - 3}</span>
              )}
            </div>
          )}

          {/* Progress Bar */}
          <div className="mb-3">
            <div className="flex justify-between text-xs text-gray-600 mb-1">
              <span className="font-medium">Progress</span>
              <span className="font-semibold text-blue-600">{task.progress_percent}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
              <div
                className="bg-gradient-to-r from-green-500 to-emerald-500 h-2 rounded-full transition-all duration-500"
                style={{ width: `${task.progress_percent}%` }}
              />
            </div>
          </div>

          <div className="mb-3 p-2 bg-gray-50 rounded-lg">
            <JourneyStrip
              currentStatus={task.status}
              createdBy={task.created_by}
              assignee={task.primary_assignee_id}
            />
          </div>
          {/* Footer */}
          <div className="flex justify-between items-center pt-3 border-t border-gray-100">
            <div className="flex items-center gap-2 text-sm">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className={isOverdue ? 'text-red-600 font-medium' : 'text-gray-500'}>
                {formatDate(task.due_date)}
              </span>
            </div>

            {/* Quick Actions */}
            <div className="flex gap-1">
              {task.status === 'TODO' && canStart && (
                <button
                  onClick={(e) => handleQuickAction(e, 'start')}
                  disabled={updating}
                  className="px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-sm rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 disabled:opacity-50 shadow-sm hover:shadow-md"
                >
                  {updating ? (
                    <span className="flex items-center gap-1">
                      <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Starting...
                    </span>
                  ) : (
                    'Start'
                  )}
                </button>
              )}

              {task.status === 'IN_PROGRESS' && (
                <button
                  onClick={(e) => handleQuickAction(e, 'complete')}
                  className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-all duration-200 shadow-sm"
                >
                  Complete
                </button>
              )}

              {task.status === 'IN_PROGRESS' && (
                <button
                  onClick={(e) => handleQuickAction(e, 'block')}
                  className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-all duration-200 shadow-sm"
                >
                  Block
                </button>
              )}
            </div>
          </div>

          {/* Created by info */}
          <div className="mt-3 pt-2 text-xs text-gray-400 flex items-center gap-2">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Created {formatDate(task.created_at)}</span>
            {task.created_by && (
              <span>by {getUserName(task.created_by)}</span>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowDeleteConfirm(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md animate-in fade-in zoom-in" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-gradient-to-r from-red-500 to-red-600 rounded-xl flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900">Delete Task</h3>
              </div>
            </div>
            <div className="p-6">
              <p className="text-gray-600">Are you sure you want to delete <span className="font-semibold">"{task.title}"</span>?</p>
              <p className="text-sm text-red-500 mt-2">This action cannot be undone.</p>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:from-red-700 hover:to-red-800 transition-all shadow-sm"
              >
                Delete Task
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Complete Confirmation Modal */}
      {showCompleteConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowCompleteConfirm(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md animate-in fade-in zoom-in" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-green-600 rounded-xl flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900">Complete Task</h3>
              </div>
            </div>
            <div className="p-6">
              <p className="text-gray-600">Are you sure you want to mark <span className="font-semibold">"{task.title}"</span> as completed?</p>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowCompleteConfirm(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleComplete}
                className="px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all shadow-sm"
              >
                Complete Task
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Block Reason Modal */}
      {showBlockPrompt && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowBlockPrompt(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md animate-in fade-in zoom-in" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-gradient-to-r from-red-500 to-red-600 rounded-xl flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900">Block Task</h3>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-gray-600">Please provide a reason for blocking <span className="font-semibold">"{task.title}"</span>:</p>
              <textarea
                value={blockReason}
                onChange={(e) => setBlockReason(e.target.value)}
                placeholder="Enter reason for blocking..."
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                autoFocus
              />
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowBlockPrompt(false);
                  setBlockReason('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleBlock}
                className="px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:from-red-700 hover:to-red-800 transition-all shadow-sm"
              >
                Block Task
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Task Modal */}
      <TaskEdit
        isOpen={showEditModal}
        task={task}
        onClose={() => setShowEditModal(false)}
        onSuccess={onRefresh}
      />
    </>
  );
}

export default TaskCard;